'use client';

import { ArrowDownToLine, Eye, Loader2, Mail, Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { HelpSupportButton } from '@/components/help-support';
import type { ColumnDefinition } from '@/components/shared/column-config-dialog';
import { ColumnConfigDialog } from '@/components/shared/column-config-dialog';
import type { DataTableColumn } from '@/components/shared/data-table';
import { DataTable } from '@/components/shared/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { Email } from '@/hooks/useEmailsData';
import { useEmailDetail, useEmailsList } from '@/hooks/useEmailsData';
import { useExportAll } from '@/hooks/useExportAll';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { formatDateTime } from '@/lib/utils';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ColumnInput } from '@/types/export';

// ── Column definitions ─────────────────────────────────────────────────────────

const DEFAULT_EMAIL_COLUMNS: ColumnDefinition[] = [
  { id: 'subject', label: 'Subject', visible: true, order: 0 },
  { id: 'customerName', label: 'Customer', visible: true, order: 1 },
  { id: 'toRecipients', label: 'To', visible: true, order: 2 },
  { id: 'ccRecipients', label: 'CC', visible: false, order: 3 },
  { id: 'sourceType', label: 'Source', visible: true, order: 4 },
  { id: 'templateName', label: 'Template', visible: true, order: 5 },
  { id: 'templateTypeId', label: 'Template Type', visible: false, order: 6 },
  { id: 'status', label: 'Status', visible: true, order: 7 },
  { id: 'sentByName', label: 'Sent By', visible: true, order: 8 },
  { id: 'sentAt', label: 'Sent At', visible: true, order: 9 },
  { id: 'createdAt', label: 'Created At', visible: false, order: 10 },
  { id: 'errorMessage', label: 'Error', visible: false, order: 11 },
  { id: 'actions', label: '', visible: true, order: 12 },
];

const sortFieldMap: Record<string, string> = {
  subject: 'Subject',
  customerName: 'CustomerName',
  toRecipients: 'ToRecipients',
  sourceType: 'SourceType',
  templateName: 'TemplateName',
  templateTypeId: 'TemplateTypeId',
  status: 'Status',
  sentByName: 'SentByName',
  sentAt: 'SentAt',
  createdAt: 'CreatedAt',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatRecipients(value: string | null | undefined): string {
  if (!value) return '-';
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.join(', ');
  } catch {
    // not JSON — fall through
  }
  return value;
}

const TEMPLATE_TYPE_LABELS: Record<number, string> = {
  1: 'Email',
  2: 'Statement',
};

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status.toLowerCase()) {
    case 'accepted':
    case 'delivered':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'failed':
      return 'destructive';
    default:
      return 'outline';
  }
}

// ── Page component ─────────────────────────────────────────────────────────────

export default function EmailingPage() {
  return (
    <ExportProvider>
      <EmailingPageInner />
    </ExportProvider>
  );
}

function EmailingPageInner() {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('all');
  const [templateTypeFilter, setTemplateTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);
  const [selectedEmailChannel, setSelectedEmailChannel] = useState<string | null>(null);

  const {
    columns: emailColumns,
    visibleColumns,
    savePreferences,
    resetToDefaults,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.EmailJobs, DEFAULT_EMAIL_COLUMNS);

  // Data fetching
  const { data: listResult, isLoading } = useEmailsList({
    page: currentPage,
    pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    sourceType: sourceTypeFilter !== 'all' ? sourceTypeFilter : undefined,
    templateTypeId: templateTypeFilter !== 'all' ? templateTypeFilter : undefined,
    sortBy: sortFieldMap[sortField] ?? 'CreatedAt',
    sortOrder: sortDirection,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const { data: emailDetail, isLoading: isDetailLoading } = useEmailDetail(selectedEmailId, selectedEmailChannel);

  const emails = listResult?.emails ?? [];
  const pagination = listResult?.pagination;

  // Column config for DataTable
  const tableColumns: DataTableColumn[] = useMemo(
    () =>
      visibleColumns
        .filter((c) => c.id !== 'actions')
        .map((c) => ({
          id: c.id,
          label: c.label,
          sortKey: sortFieldMap[c.id] ? c.id : undefined,
        }))
        .concat([{ id: 'actions', label: '', sortKey: undefined }]),
    [visibleColumns],
  );

  // Handlers
  const handleSort = useCallback(
    (field: string) => {
      if (field === sortField) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
      setCurrentPage(1);
    },
    [sortField],
  );

  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleViewDetail = useCallback((id: number, channel: string) => {
    setSelectedEmailId(id);
    setSelectedEmailChannel(channel);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedEmailId(null);
    setSelectedEmailChannel(null);
  }, []);

  const exportColumnNameMap: Record<string, string> = useMemo(
    () => ({
      ...sortFieldMap,
    }),
    [],
  );

  const getExportColumns = useCallback(
    (): ColumnInput[] =>
      emailColumns
        .filter((col) => col.visible && col.id !== 'actions' && col.id in exportColumnNameMap)
        .map((col) => ({
          name: exportColumnNameMap[col.id],
          displayName: col.label,
        })),
    [emailColumns, exportColumnNameMap],
  );

  const getAllExportColumns = useCallback(
    (): ColumnInput[] =>
      DEFAULT_EMAIL_COLUMNS.filter((col) => col.id !== 'actions' && col.id in exportColumnNameMap).map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      })),
    [exportColumnNameMap],
  );

  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    const filter: Record<string, unknown> = {};
    if (statusFilter !== 'all') filter.status = { eq: statusFilter };
    if (sourceTypeFilter !== 'all') filter.sourceType = { eq: sourceTypeFilter };
    if (templateTypeFilter !== 'all') {
      filter.templateTypeId =
        templateTypeFilter === '__none__' ? { isBlank: true } : { eq: Number(templateTypeFilter) };
    }
    if (dateFrom) {
      filter.createdAt = {
        ...((filter.createdAt as Record<string, unknown>) ?? {}),
        ge: dateFrom,
      };
    }
    if (dateTo) {
      const inclusiveEnd = new Date(dateTo);
      inclusiveEnd.setDate(inclusiveEnd.getDate() + 1);
      const ltDate = inclusiveEnd.toISOString().split('T')[0];
      filter.createdAt = {
        ...((filter.createdAt as Record<string, unknown>) ?? {}),
        lt: ltDate,
      };
    }
    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [statusFilter, sourceTypeFilter, templateTypeFilter, dateFrom, dateTo]);

  const getExportSummary = useCallback((): string | undefined => debouncedSearch || undefined, [debouncedSearch]);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'Emails',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `emails-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'Emails',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `emails-all-columns-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportCurrentView = () => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleExportAllColumns = () => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Render cell
  const renderCell = useCallback(
    (columnId: string, item: Email) => {
      switch (columnId) {
        case 'subject':
          return <span className="font-medium truncate max-w-[300px] block">{item.subject}</span>;
        case 'customerName':
          return item.customerName;
        case 'toRecipients':
          return <span className="truncate max-w-[200px] block">{formatRecipients(item.toRecipients)}</span>;
        case 'ccRecipients':
          return item.ccRecipients ? (
            <span className="truncate max-w-[200px] block">{formatRecipients(item.ccRecipients)}</span>
          ) : (
            '-'
          );
        case 'sourceType':
          return <Badge variant="outline">{item.sourceType}</Badge>;
        case 'templateName':
          return item.templateName ?? '-';
        case 'templateTypeId':
          return item.templateTypeId !== null ? (
            <Badge variant="outline">{TEMPLATE_TYPE_LABELS[item.templateTypeId] ?? item.templateTypeId}</Badge>
          ) : (
            '-'
          );
        case 'status':
          return <Badge variant={getStatusBadgeVariant(item.status)}>{item.status}</Badge>;
        case 'sentByName':
          return item.sentByName;
        case 'sentAt':
          return item.sentAt ? formatDateTime(item.sentAt) : '-';
        case 'createdAt':
          return formatDateTime(item.createdAt);
        case 'errorMessage':
          return item.errorMessage ? (
            <span className="text-destructive text-sm truncate max-w-[200px] block">{item.errorMessage}</span>
          ) : (
            '-'
          );
        case 'actions':
          return (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleViewDetail(item.id, item.emailChannel)}
              title="View email details"
            >
              <Eye className="h-4 w-4" />
            </Button>
          );
        default:
          return null;
      }
    },
    [handleViewDetail],
  );

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-baseline gap-2">
          <Mail className="h-6 w-6" />
          <h1 className="text-2xl font-semibold tracking-tight">Email History</h1>
          <span className="text-sm text-muted-foreground">View all sent emails across the system</span>
        </div>
        <HelpSupportButton screenId="emailing" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search subject, recipient, customer..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-8"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Accepted">Accepted</SelectItem>
            <SelectItem value="Delivered">Delivered</SelectItem>
            <SelectItem value="Failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={sourceTypeFilter}
          onValueChange={(v) => {
            setSourceTypeFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="Customer">Customer</SelectItem>
            <SelectItem value="Collection">Collection</SelectItem>
            <SelectItem value="Promise">Promise</SelectItem>
            <SelectItem value="Dispute">Dispute</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={templateTypeFilter}
          onValueChange={(v) => {
            setTemplateTypeFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-[190px]">
            <SelectValue placeholder="Template Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Template Types</SelectItem>
            <SelectItem value="1">Email</SelectItem>
            <SelectItem value="2">Statement</SelectItem>
            <SelectItem value="__none__">No Template</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">From</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setCurrentPage(1);
            }}
            className="w-[150px]"
          />
        </div>
        <div className="flex items-center gap-1">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">To</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setCurrentPage(1);
            }}
            className="w-[150px]"
          />
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowDownToLine className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCurrentView}>Export Current View</DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ColumnConfigDialog columns={emailColumns} onApply={savePreferences} onReset={resetToDefaults} />
        </div>
      </div>

      {/* Table */}
      <Card className="flex flex-col flex-1 min-h-0">
        <CardContent className="p-0 flex flex-col flex-1 min-h-0">
          <DataTable<Email>
            columns={tableColumns}
            data={emails}
            getRowId={(item) => item.id}
            renderCell={renderCell}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isLoading={isLoading}
            initialColumnOrder={columnOrder}
            initialColumnWidths={columnWidths}
            initialPinnedLeft={pinnedLeft}
            initialPinnedRight={pinnedRight}
            onColumnOrderChange={setColumnOrder}
            onColumnWidthsChange={setColumnWidths}
            onPinnedChange={(left, right) => setPinned(left, right)}
            onResetColumnOrder={resetColumnOrder}
            onResetColumnWidths={resetColumnWidths}
            emptyContent={
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Mail className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">No emails found</h3>
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
              </div>
            }
            pagination={
              pagination
                ? {
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    pageSize: pagination.pageSize,
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                  }
                : undefined
            }
          />
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseDetail();
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
            <DialogDescription>{emailDetail ? emailDetail.subject : 'Loading...'}</DialogDescription>
          </DialogHeader>
          {isDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : emailDetail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge variant={getStatusBadgeVariant(emailDetail.status)}>{emailDetail.status}</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Source</Label>
                  <div className="mt-1">
                    <Badge variant="outline">{emailDetail.sourceType}</Badge>
                    {!!emailDetail.sourceId && (
                      <span className="ml-2 text-sm text-muted-foreground">#{emailDetail.sourceId}</span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Subject</Label>
                <p className="mt-1 font-medium">{emailDetail.subject}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">To</Label>
                  <p className="mt-1">{formatRecipients(emailDetail.toRecipients)}</p>
                </div>
                {!!emailDetail.ccRecipients && (
                  <div>
                    <Label className="text-muted-foreground">CC</Label>
                    <p className="mt-1">{formatRecipients(emailDetail.ccRecipients)}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Customer</Label>
                  <p className="mt-1">
                    {emailDetail.customerName}
                    {!!emailDetail.customerAccountNumber && (
                      <span className="text-muted-foreground ml-1">({emailDetail.customerAccountNumber})</span>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Template</Label>
                  <p className="mt-1">{emailDetail.templateName ?? '-'}</p>
                </div>
              </div>

              {emailDetail.templateTypeId !== null && (
                <div>
                  <Label className="text-muted-foreground">Template Type</Label>
                  <p className="mt-1">
                    <Badge variant="outline">
                      {TEMPLATE_TYPE_LABELS[emailDetail.templateTypeId] ?? emailDetail.templateTypeId}
                    </Badge>
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Sent By</Label>
                  <p className="mt-1">
                    {emailDetail.sentByName}
                    {!!emailDetail.sentByEmail && (
                      <span className="text-muted-foreground ml-1">({emailDetail.sentByEmail})</span>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{emailDetail.sentAt ? 'Sent At' : 'Created At'}</Label>
                  <p className="mt-1">{formatDateTime(emailDetail.sentAt ?? emailDetail.createdAt)}</p>
                </div>
              </div>

              {!!emailDetail.errorMessage && (
                <div>
                  <Label className="text-destructive">Error Message</Label>
                  <p className="mt-1 text-destructive text-sm rounded-md border border-destructive/20 bg-destructive/5 p-3">
                    {emailDetail.errorMessage}
                  </p>
                </div>
              )}

              {!!emailDetail.htmlBody && (
                <div>
                  <Label className="text-muted-foreground">Email Body</Label>
                  <div className="mt-1 rounded-md border overflow-hidden">
                    <iframe
                      srcDoc={emailDetail.htmlBody}
                      sandbox=""
                      title="Email body preview"
                      className="w-full min-h-[300px] bg-white"
                      style={{ border: 'none' }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
